import { useEffect, useState, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ProductGallery, ProductCard, SetContentsSection, ProductColorConfigurator } from '@/components/product';
import { AnimatedSection, WhatsAppButton } from '@/components/common';
import { StickyMicroForm } from '@/components/lead';
import { useProducts } from '@/hooks';
import {
  Users,
  Maximize2,
  ShieldCheck,
  ArrowRight,
  RotateCcw
} from 'lucide-react';
import type { ProductType, SetTechnicalSpecs, ProductColor, ProductImage } from '@/types';

// --- Sub Components ---

const FeatureBadge = ({ icon: Icon, label, value }: { icon: any; label: string; value: string }) => (
  <div className="flex flex-col items-center justify-center p-3 sm:p-4 bg-stone-50 rounded-lg border border-stone-100 text-center space-y-2 group hover:border-stone-200 transition-colors">
    <Icon className="w-5 h-5 text-gray-400 group-hover:text-charcoal transition-colors" />
    <div>
      <span className="block text-[10px] uppercase tracking-widest text-gray-400 font-bold mb-0.5">{label}</span>
      <span className="block text-xs sm:text-sm font-medium text-charcoal">{value}</span>
    </div>
  </div>
);

// Helper component for a single specs table section
const SpecsTableSection = ({ title, rows }: { title?: string; rows: { label: string; value: React.ReactNode }[] }) => {
  if (rows.length === 0) return null;

  return (
    <div className="w-full overflow-hidden rounded-md border border-gray-200">
      {title && (
        <div className="px-4 py-2 font-semibold text-sm bg-gray-50 text-gray-700 border-b border-gray-200">
          {title}
        </div>
      )}
      <table className="w-full text-sm text-left">
        <tbody>
          {rows.map((row, index) => (
            <tr key={index} className={`bg-white even:bg-gray-50 hover:bg-gray-50 transition-colors ${index < rows.length - 1 ? 'border-b border-gray-200' : ''}`}>
              <td className="py-3 px-4 font-semibold text-charcoal/70 w-2/5 align-top bg-opacity-50 border-r border-gray-100">
                {row.label}
              </td>
              <td className="py-3 px-4 text-charcoal font-medium">
                {row.value}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

// Unified Specs Table Component
const SpecsTable = ({ dimensions, productType, techSpecs, materials }: { dimensions: any; productType: ProductType; techSpecs?: any; materials?: string[] }) => {

  // For Set products: Show separate tables for common specs, table dimensions, and chair dimensions
  if (productType === 'set') {
    const commonRows: { label: string; value: React.ReactNode }[] = [];
    const tableRows: { label: string; value: React.ReactNode }[] = [];
    const chairRows: { label: string; value: React.ReactNode }[] = [];

    // Common specs for Sets
    if (materials && materials.length > 0) {
      commonRows.push({ label: 'Malzeme & Materyal', value: materials.join(', ') });
    }
    if (techSpecs?.usageArea) commonRows.push({ label: 'Kullanım Alanı', value: techSpecs.usageArea });
    if (techSpecs?.capacity) commonRows.push({ label: 'Kapasite', value: techSpecs.capacity });
    if (techSpecs?.seatingCapacity) commonRows.push({ label: 'Oturma Kapasitesi', value: `${techSpecs.seatingCapacity} Kişilik` });

    // Takım İçeriği
    if (techSpecs?.tableCount || techSpecs?.chairCount) {
      const parts = [];
      if (techSpecs.tableCount) parts.push(`${techSpecs.tableCount} Masa`);
      if (techSpecs.chairCount) parts.push(`${techSpecs.chairCount} Sandalye`);
      commonRows.push({ label: 'Takım İçeriği', value: parts.join(' + ') });
    } else if (techSpecs?.setContent) {
      commonRows.push({ label: 'Takım İçeriği', value: techSpecs.setContent });
    }

    // Table dimensions (from tableDimensions nested object)
    const tableDims = dimensions?.tableDimensions;
    const unit = dimensions?.unit || 'cm';
    if (tableDims) {
      if (tableDims.width) tableRows.push({ label: 'Genişlik', value: `${tableDims.width} ${unit}` });
      if (tableDims.depth) tableRows.push({ label: 'Derinlik', value: `${tableDims.depth} ${unit}` });
      if (tableDims.height) tableRows.push({ label: 'Yükseklik', value: `${tableDims.height} ${unit}` });
      if (tableDims.tableHeight) tableRows.push({ label: 'Masa Yüksekliği', value: `${tableDims.tableHeight} ${unit}` });
    }

    // Chair dimensions (from chairDimensions nested object)
    const chairDims = dimensions?.chairDimensions;
    if (chairDims) {
      if (chairDims.width) chairRows.push({ label: 'Genişlik', value: `${chairDims.width} ${unit}` });
      if (chairDims.depth) chairRows.push({ label: 'Derinlik', value: `${chairDims.depth} ${unit}` });
      if (chairDims.height) chairRows.push({ label: 'Yükseklik', value: `${chairDims.height} ${unit}` });
      if (chairDims.seatHeight) chairRows.push({ label: 'Oturum Yüksekliği', value: `${chairDims.seatHeight} ${unit}` });
    }

    // If no data at all, return null
    if (commonRows.length === 0 && tableRows.length === 0 && chairRows.length === 0) return null;

    return (
      <div className="w-full mt-8">
        {/* Desktop: 3 tables side by side - no width constraints */}
        <div className="hidden md:grid md:grid-cols-3 gap-4" style={{ minWidth: 'max-content' }}>
          {commonRows.length > 0 && (
            <SpecsTableSection rows={commonRows} />
          )}
          {tableRows.length > 0 && (
            <SpecsTableSection title="Masa Ölçüleri" rows={tableRows} />
          )}
          {chairRows.length > 0 && (
            <SpecsTableSection title="Sandalye Ölçüleri" rows={chairRows} />
          )}
        </div>

        {/* Mobile: stacked vertically */}
        <div className="md:hidden space-y-4">
          {commonRows.length > 0 && (
            <SpecsTableSection rows={commonRows} />
          )}
          {tableRows.length > 0 && (
            <SpecsTableSection title="Masa Ölçüleri" rows={tableRows} />
          )}
          {chairRows.length > 0 && (
            <SpecsTableSection title="Sandalye Ölçüleri" rows={chairRows} />
          )}
        </div>
      </div>
    );
  }

  // For Chair/Table: Standard single table
  const rows: { label: string; value: React.ReactNode }[] = [];

  // 1. Materials (First as they are often primary features)
  if (materials && materials.length > 0) {
    rows.push({ label: 'Malzeme & Materyal', value: materials.join(', ') });
  }

  // 2. Dimensions
  if (dimensions) {
    if (dimensions.width) rows.push({ label: 'Genişlik', value: `${dimensions.width} ${dimensions.unit || 'cm'}` });
    if (dimensions.depth) rows.push({ label: 'Derinlik', value: `${dimensions.depth} ${dimensions.unit || 'cm'}` });
    if (dimensions.height) rows.push({ label: 'Yükseklik', value: `${dimensions.height} ${dimensions.unit || 'cm'}` });
    if (productType === 'chair' && dimensions.seatHeight) rows.push({ label: 'Oturum Yüksekliği', value: `${dimensions.seatHeight} ${dimensions.unit || 'cm'}` });
    if (productType === 'table' && dimensions.tableHeight) rows.push({ label: 'Masa Yüksekliği', value: `${dimensions.tableHeight} ${dimensions.unit || 'cm'}` });
  }

  // 3. Technical Specs (Strict Filter: Only show if true/present)
  if (techSpecs) {
    if (techSpecs.weight) {
      const weightValue = String(techSpecs.weight).replace(/[^\d.]/g, '');
      rows.push({ label: 'Ürün Ağırlığı', value: `${weightValue} kg` });
    }
    if (techSpecs.usageArea) rows.push({ label: 'Kullanım Alanı', value: techSpecs.usageArea });
    if (techSpecs.capacity) rows.push({ label: 'Kapasite', value: techSpecs.capacity });
    if (techSpecs.seatingCapacity) rows.push({ label: 'Oturma Kapasitesi', value: `${techSpecs.seatingCapacity} Kişilik` });

    // Table Specifics
    if (productType === 'table') {
      if (techSpecs.extendable) rows.push({ label: 'Açılabilir', value: 'Evet' });
      if (techSpecs.extendedWidth) rows.push({ label: 'Açık Genişlik', value: `${techSpecs.extendedWidth} cm` });
      if (techSpecs.tableShape) {
        const shapeMap: Record<string, string> = { 'round': 'Yuvarlak', 'rectangle': 'Dikdörtgen', 'square': 'Kare', 'oval': 'Oval' };
        rows.push({ label: 'Masa Formu', value: shapeMap[techSpecs.tableShape as string] || techSpecs.tableShape });
      }
    }

    // Chair Specifics
    if (productType === 'chair') {
      if (techSpecs.stackable) rows.push({ label: 'İstiflenebilir', value: `Evet ${techSpecs.stackCount ? `(${techSpecs.stackCount} Adet)` : ''}` });
      if (techSpecs.armrest) rows.push({ label: 'Kolçak', value: 'Var' });
    }
  }

  if (rows.length === 0) return null;

  return (
    <div className="w-full mt-8 overflow-hidden rounded-md border border-gray-200">
      <table className="w-full text-sm text-left">
        <tbody className="divide-y divide-gray-200">
          {rows.map((row, index) => (
            <tr key={index} className="bg-white even:bg-gray-50 hover:bg-gray-50 transition-colors">
              <td className="py-3 px-4 font-semibold text-charcoal/70 w-2/5 align-top bg-opacity-50 border-r border-gray-100">
                {row.label}
              </td>
              <td className="py-3 px-4 text-charcoal font-medium">
                {row.value}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};


export const ProductDetailPage = () => {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [selectedColor, setSelectedColor] = useState<ProductColor | null>(null);

  const { useProductDetail, useRelatedProducts } = useProducts();
  const { product, isLoading, isError } = useProductDetail(slug);
  const { relatedProducts, isLoading: isRelatedLoading } = useRelatedProducts(product?.id);

  // Determine product type from database field or fallback
  const productType: ProductType = product?.productType || 'chair';
  const isTable = productType === 'table';
  const isSet = productType === 'set';

  // Type-safe technical specs access
  const techSpecs = product?.technicalSpecs as any;
  const setContents = isSet && techSpecs?.setContents ? (techSpecs as SetTechnicalSpecs).setContents : undefined;

  // Scroll to top on slug change
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  // Handle 404
  useEffect(() => {
    if (isError) {
      navigate('/products');
    }
  }, [isError, navigate]);

  // SEO Title Update
  useEffect(() => {
    if (product) {
      document.title = `${product.title} | Turco Sandalye`;
    }
  }, [product]);

  // Set initial selected color
  useEffect(() => {
    if (product?.colors && product.colors.length > 0 && !selectedColor) {
      setSelectedColor(product.colors[0]);
    }
  }, [product, selectedColor]);

  // Construct gallery images based on selected color
  const displayedImages = useMemo(() => {
    if (!product) return [];

    let baseImages = [...product.images];

    // If a color is selected and it has an image
    if (selectedColor?.image) {
      // Check if this image is already in the list to avoid duplication
      // (This is a simple check, could be more robust with IDs if we had them for color images)
      const existingIndex = baseImages.findIndex(img => img.url === selectedColor.image);

      if (existingIndex !== -1) {
        // If it exists, move it to front
        const [existingImage] = baseImages.splice(existingIndex, 1);
        baseImages = [existingImage, ...baseImages];
      } else {
        // Create a temporary image object
        const colorImage: ProductImage = {
          id: `color-${selectedColor.hex}`, // Temporary unique ID
          url: selectedColor.image,
          position: -1,
          isPrimary: true,
          alt: `${product.title} - ${selectedColor.name}`,
        };
        baseImages = [colorImage, ...baseImages];
      }
    }

    return baseImages;
  }, [product, selectedColor]);

  if (isLoading) {
    return (
      <div className="container mx-auto px-4 py-32 space-y-12">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          <div className="aspect-[4/5] bg-gray-100 animate-pulse rounded-sm" />
          <div className="space-y-6">
            <div className="h-4 w-24 bg-gray-100 rounded animate-pulse" />
            <div className="h-12 w-3/4 bg-gray-100 rounded animate-pulse" />
            <div className="h-24 w-full bg-gray-100 rounded animate-pulse" />
          </div>
        </div>
      </div>
    );
  }

  if (!product) return null;

  // Type-specific badges
  const getFeatureBadges = () => {
    if (isSet) {
      return (
        <>
          {techSpecs?.seatingCapacity && (
            <FeatureBadge icon={Users} label="Kapasite" value={`${techSpecs.seatingCapacity} Kişilik`} />
          )}
        </>
      );
    }

    if (isTable) {
      return (
        <>
          {techSpecs?.seatingCapacity && (
            <FeatureBadge icon={Users} label="Kapasite" value={`${techSpecs.seatingCapacity} Kişilik`} />
          )}
          {product.dimensions?.width && (
            <FeatureBadge icon={Maximize2} label="Genişlik" value={`${product.dimensions.width} cm`} />
          )}
          {techSpecs?.extendable && techSpecs?.extendedWidth && (
            <FeatureBadge icon={RotateCcw} label="Açılır" value={`${techSpecs.extendedWidth} cm`} />
          )}
        </>
      );
    }

    // Chair
    return null;
  };

  // Type-specific description
  const getDefaultDescription = () => {
    if (isSet) {
      return 'Masa ve sandalyelerin mükemmel uyumu. Mekanınız için özenle seçilmiş, birbiriyle tam uyumlu parçalardan oluşan özel koleksiyon.';
    }
    if (isTable) {
      return 'Modern mekanlar için tasarlanmış, doğal dokuların zarafetini taşıyan signature masa serisi. Zamansız tasarımı ile her mekana uyum sağlar.';
    }
    return 'Form ve fonksiyonun mükemmel dengesi. Uzun oturumlar için tasarlanmış ergonomik yapı ve üstün malzeme kalitesi.';
  };

  return (
    <>
      {/* WhatsApp Floating Button - Ana sayfadaki gibi */}
      <WhatsAppButton message={`Merhaba, ${product.title} hakkında bilgi almak istiyorum.`} />

      <div className="min-h-screen bg-white pt-32 lg:pt-40">

        {/* Breadcrumb - Cleaned up per user request */}
        <div className="container mx-auto px-4 mb-8">
          <nav className="text-xs text-gray-500 font-medium uppercase tracking-wider flex items-center space-x-2">
            <span onClick={() => navigate('/products')} className="cursor-pointer hover:text-charcoal transition-colors">Koleksiyon</span>
            <span className="text-gray-300">/</span>
            {product.categories?.[0] && (
              <>
                <span
                  onClick={() => navigate(`/products?categorySlug=${product.categories[0].slug}`)}
                  className="cursor-pointer text-gray-500 hover:text-charcoal transition-colors"
                >
                  {product.categories[0].name}
                </span>
                <span className="text-gray-300">/</span>
              </>
            )}
            <span className="text-charcoal font-bold">{product.title}</span>
          </nav>
        </div>

        <div className="container mx-auto px-4 pb-12">
          {/* Two column product layout */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 xl:gap-16">

            {/* LEFT: Gallery - sticky on scroll */}
            <div className="lg:sticky lg:top-32 lg:self-start overflow-visible">
              <ProductGallery images={displayedImages} />

              {/* Desktop: Technical Specs Table Section */}
              <div className="hidden lg:block w-full mt-10 overflow-visible">
                <div className="flex flex-col overflow-visible">
                  {/* Header */}
                  <h3 className="font-display text-xl text-charcoal mb-2">Teknik Özellikler</h3>

                  {/* Full width divider matching table width */}
                  <div className="w-full h-[2px] bg-gray-300 mb-6" />

                  {/* Table - allow overflow for Set products */}
                  <div className="overflow-visible">
                    <SpecsTable
                      dimensions={product.dimensions}
                      productType={productType}
                      techSpecs={techSpecs}
                      materials={product.materials}
                    />
                  </div>
                </div>
              </div>

              {/* Feature badges - Mobile only */}
              <div className="mt-6 lg:hidden grid grid-cols-3 gap-3">
                {getFeatureBadges()}
              </div>
            </div>

            {/* RIGHT: Product Info */}
            <div className="relative z-10 flex flex-col gap-6 lg:gap-8">

              {/* Product Type Badge + Category + Title */}
              <div className="space-y-4 border-b border-gray-100 pb-6 lg:pb-8">

                {/* Product Title */}
                <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl xl:text-6xl text-charcoal leading-[1.1] tracking-tight">
                  {product.title}
                </h1>

                <div className="text-base lg:text-lg text-gray-600 font-light leading-relaxed opacity-90">
                  {product.description || getDefaultDescription()}
                </div>

              </div>

              {/* Set Contents Section - Only for Set products */}
              {isSet && setContents && setContents.length > 0 && (
                <SetContentsSection
                  contents={setContents}
                  totalPieces={techSpecs?.totalPieces}
                />
              )}

              {/* Key Features Grid (Desktop) */}
              <div className="hidden lg:grid grid-cols-3 gap-3">
                {getFeatureBadges()}
              </div>

              {/* Action Area */}
              <div className="space-y-4 pt-4">
                <button
                  onClick={() => setIsFormOpen(true)}
                  className="w-full bg-charcoal text-white h-14 flex items-center justify-center gap-2 text-sm font-bold uppercase tracking-[0.1em] hover:bg-black transition-all group shadow-lg shadow-charcoal/10"
                >
                  Fiyat & Bilgi Al
                  <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </button>


                <div className="flex items-center justify-center gap-2 text-xs text-gray-400">
                  <ShieldCheck className="w-3 h-3" />
                  <span>Proje bazlı toplu alımlar için özel iskontolar mevcuttur.</span>
                </div>
              </div>

              <StickyMicroForm
                product={product}
                isOpen={isFormOpen}
                onClose={() => setIsFormOpen(false)}
                selectedColorName={selectedColor?.name}
              />

              {/* Mobile: Technical Specs Table */}
              <div className="lg:hidden pt-8">
                <h3 className="font-display text-xl text-charcoal mb-2">Teknik Özellikler</h3>
                <div className="w-full h-[2px] bg-gray-300 mb-6" />
                <SpecsTable
                  dimensions={product.dimensions}
                  productType={productType}
                  techSpecs={techSpecs}
                  materials={product.materials}
                />
              </div>

              {/* Color Configuration Section - Only show if product has colors */}
              {product.colors && product.colors.length > 0 && (
                <div className="pt-8">
                  {/* Section Header */}
                  <h3 className="font-display text-xl text-charcoal mb-2">Rengi ve Doku Tasarla</h3>
                  <div className="w-full h-[2px] bg-gray-300 mb-6" />

                  <ProductColorConfigurator
                    colors={product.colors}
                    onColorSelect={setSelectedColor}
                    initialColor={selectedColor || undefined}
                    productImages={product.images}
                  />
                </div>
              )}

            </div>
          </div>
        </div>

        {/* Related Products Section */}
        {relatedProducts.length > 0 && (
          <div className="bg-stone-50/50 py-24 border-t border-gray-100">
            <div className="container mx-auto px-4">
              <AnimatedSection>
                <div className="flex justify-between items-end mb-12">
                  <div>
                    <h2 className="font-display text-3xl text-charcoal mb-2">
                      {isTable ? 'Bu Masayla Uyumlu Sandalyeler' : isSet ? 'Benzer Takımlar' : 'Benzer Tasarımlar'}
                    </h2>
                    <p className="text-gray-500 font-light text-sm">Mekanınızı tamamlayacak parçalar</p>
                  </div>
                  <button
                    onClick={() => navigate('/products')}
                    className="hidden md:inline-flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-charcoal hover:text-gold transition-colors group"
                  >
                    Tüm Koleksiyon
                    <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                  </button>
                </div>

                {isRelatedLoading ? (
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                    {[1, 2, 3, 4].map(i => <div key={i} className="aspect-[3/4] bg-gray-200 rounded animate-pulse" />)}
                  </div>
                ) : (
                  <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8">
                    {relatedProducts.slice(0, 4).map((p) => (
                      <ProductCard key={p.id} product={p} />
                    ))}
                  </div>
                )}
              </AnimatedSection>
            </div>
          </div>
        )}
      </div>
    </>
  );
};
